import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useUpsertGrant } from '../../hooks/use-admin-grants.js';
import type { DataScopeClause, UpsertGrantBody } from '../../lib/admin-client.js';

const s = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
    overflow: 'hidden',
  },
  header: { padding: '1rem 1.5rem', borderBottom: `1px solid ${tokens.colors.borderDark}` },
  title: { fontSize: '1rem', fontWeight: 600, color: tokens.colors.textDark, margin: 0 },
  body: { padding: '1.5rem', display: 'flex', flexDirection: 'column' as const, gap: '1rem' },
  group: { display: 'flex', flexDirection: 'column' as const, gap: '0.375rem' },
  label: { fontSize: '0.875rem', fontWeight: 500, color: tokens.colors.textDark },
  input: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  select: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    outline: 'none',
    width: '100%',
  },
  btn: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnSecondary: {
    backgroundColor: 'transparent',
    color: tokens.colors.textDark,
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  scopeRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  scopeInput: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.375rem 0.625rem',
    fontSize: '0.8125rem',
    outline: 'none',
    flex: 1,
  },
};

interface GrantFormProps {
  tenantId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

type ClauseRow = DataScopeClause & { _key: string };

export function GrantForm({ tenantId, onSuccess, onCancel }: GrantFormProps) {
  const [tenantReportId, setTenantReportId] = useState('');
  const [subjectType, setSubjectType] = useState<'role' | 'user'>('role');
  const [subjectId, setSubjectId] = useState('');
  const [level, setLevel] = useState<'view' | 'execute' | 'full'>('view');
  const [clauses, setClauses] = useState<ClauseRow[]>([]);

  const upsert = useUpsertGrant(tenantId);

  function addClause() {
    setClauses((prev) => [...prev, { field: '', op: 'eq', value: '', _key: crypto.randomUUID() }]);
  }

  function updateClause(idx: number, key: keyof DataScopeClause, val: string) {
    setClauses((prev) => prev.map((c, i) => (i === idx ? { ...c, [key]: val } : c)));
  }

  function removeClause(idx: number) {
    setClauses((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleSubmit() {
    const body: UpsertGrantBody = {
      tenant_report_id: tenantReportId,
      subject_type: subjectType,
      subject_id: subjectId,
      level,
      data_scope: clauses
        .filter((c) => c.field.trim().length > 0)
        .map(({ _key: _, ...rest }) => rest),
    };
    upsert.mutate(body, { ...(onSuccess && { onSuccess }) });
  }

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h2 style={s.title}>New Grant</h2>
      </div>
      <div style={s.body}>
        <div style={s.group}>
          <label style={s.label} htmlFor="grant-report">
            Tenant Report ID
          </label>
          <input
            id="grant-report"
            style={s.input}
            value={tenantReportId}
            onChange={(e) => setTenantReportId(e.target.value)}
            placeholder="trp_…"
            aria-label="Tenant report ID"
          />
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <div style={{ ...s.group, flex: 1 }}>
            <label style={s.label} htmlFor="grant-subject-type">
              Subject type
            </label>
            <select
              id="grant-subject-type"
              style={s.select}
              value={subjectType}
              onChange={(e) => setSubjectType(e.target.value as 'role' | 'user')}
              aria-label="Subject type"
            >
              <option value="role">Role</option>
              <option value="user">User</option>
            </select>
          </div>
          <div style={{ ...s.group, flex: 2 }}>
            <label style={s.label} htmlFor="grant-subject-id">
              Subject ID
            </label>
            <input
              id="grant-subject-id"
              style={s.input}
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              placeholder="rol_… or usr_…"
              aria-label="Subject ID"
            />
          </div>
        </div>
        <div style={s.group}>
          <label style={s.label} htmlFor="grant-level">
            Level
          </label>
          <select
            id="grant-level"
            style={s.select}
            value={level}
            onChange={(e) => setLevel(e.target.value as 'view' | 'execute' | 'full')}
            aria-label="Grant level"
          >
            <option value="view">View</option>
            <option value="execute">Execute</option>
            <option value="full">Full</option>
          </select>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={s.label}>Data scope clauses</span>
            <button
              type="button"
              style={{ ...s.btnSecondary, padding: '0.25rem 0.625rem', fontSize: '0.8125rem' }}
              onClick={addClause}
              aria-label="Add data scope clause"
            >
              + Add clause
            </button>
          </div>
          {clauses.map((c, idx) => (
            <div key={c._key} style={{ ...s.scopeRow, marginBottom: '0.5rem' }}>
              <input
                style={s.scopeInput}
                value={c.field}
                onChange={(e) => updateClause(idx, 'field', e.target.value)}
                placeholder="field"
                aria-label="Clause field"
              />
              <select
                style={{ ...s.scopeInput, flex: 'none', width: '120px' }}
                value={c.op}
                onChange={(e) => updateClause(idx, 'op', e.target.value)}
                aria-label="Clause operator"
              >
                {['eq', 'neq', 'in', 'starts_with', 'ends_with', 'contains'].map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <input
                style={s.scopeInput}
                value={String(c.value)}
                onChange={(e) => updateClause(idx, 'value', e.target.value)}
                placeholder="value"
                aria-label="Clause value"
              />
              <button
                type="button"
                style={{
                  background: 'none',
                  border: 'none',
                  color: tokens.colors.textMutedDark,
                  cursor: 'pointer',
                  fontSize: '1rem',
                }}
                onClick={() => removeClause(idx)}
                aria-label="Remove clause"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {upsert.isError && (
          <p style={{ color: tokens.colors.error, fontSize: '0.875rem' }} role="alert">
            Error saving grant.
          </p>
        )}

        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: `1px solid ${tokens.colors.borderDark}`,
          }}
        >
          <button
            type="button"
            style={{ ...s.btn, opacity: upsert.isPending ? 0.7 : 1 }}
            onClick={handleSubmit}
            disabled={upsert.isPending || !tenantReportId || !subjectId}
            aria-label="Save grant"
          >
            {upsert.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" style={s.btnSecondary} onClick={onCancel} aria-label="Cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
